<template>
	<Wizard
		ref="wizard"
		:disabled-continue="disableContinueButton"
		:last-step-text="$t('web.profile.create.account.button')"
		@submit="submit"
	>
		<template #step1>
			<ProfileWizardDestinations
				:stickers="wishlistStickers"
				:is-x-s="isXS"
				placeholder="wishlist_placeholder_question"
				:travel-destinations="wishList"
				title="wishlistSetup_heading"
				description="wishlistSetup_subheading"
				type="wishlist"
				@update-destinations="addWishlist"
				@delete-destination="deleteWishlistAdded"
				@skip="skipStep"
			/>
		</template>
		<template #step2>
			<ProfileWizardDestinations
				:stickers="visitedStickers"
				:is-x-s="isXS"
				placeholder="visitedDestinations_placeholder_question"
				:travel-destinations="visitedList"
				title="pastAdventuresSetup_heading"
				description="pastAdventuresSetup_subheading"
				type="visited"
				@update-destinations="addVisitedList"
				@delete-destination="deleteVisitedAdded"
				@skip="skipStep"
			/>
		</template>
		<template #step3>
			<ProfileWizardForm
				:profile="form"
				:form-errors="formErrors"
				:is-x-s="isXS"
				@change="handleProfile"
			/>
		</template>
	</Wizard>
</template>

<script setup lang="ts">
	import { storeToRefs } from 'pinia'
	import { onMounted, ref, computed } from 'vue'
	import { ProfileActions } from '~/actions/profile/action'
	import { ProfileDestinationsActions } from '~/actions/profile/destinations'
	import traveler from '~/assets/profileWizard/profile-visited-sticker-traveler.svg'
	import worldwide from '~/assets/profileWizard/profile-visited-sticker-worldwide.svg'
	import desert from '~/assets/profileWizard/profile-wishlist-sticker-desert.svg'
	import island from '~/assets/profileWizard/profile-wishlist-sticker-island.svg'
	import passporter from '~/assets/profileWizard/profile-wishlist-sticker-passporter.svg'

	import visitedDesert from '~/assets/profileWizard/profile-visited-sticker-desert.svg'
	import visitedPassporter from '~/assets/profileWizard/profile-visited-sticker-passporter.svg'

	import village from '~/assets/profileWizard/profile-wishlist-sticker-village.svg'
	import { Profile } from '~/models/Profile'
	import { ProfileDestination } from '~/models/ProfileDestination'
	import { useDeviceStore } from '~/store/device'

	const deviceStore = useDeviceStore()
	const { isXS } = storeToRefs(deviceStore)

	const wizard = ref()

	const wishList = ref<ProfileDestination[]>([])
	const visitedList = ref<ProfileDestination[]>([])

	const wishlistStickers: string[] = [passporter, island, desert, village]
	const visitedStickers: string[] = [traveler, worldwide, visitedDesert, visitedPassporter]
	const profile = ref<Profile>()
	const wishlistAdded = ref<ProfileDestination[]>([])
	const visitedAdded = ref<ProfileDestination[]>([])

	profile.value = await ProfileActions.getProfile()
	const formErrors = ref<Partial<Record<keyof Profile, string>>>({})
	const emit = defineEmits(['close'])

	const form = ref<Profile>({
		id: profile.value?.id,
		name: profile.value.name,
		birthYear: profile.value.birthYear,
		country: profile.value.country,
		countryName: profile.value.countryName,
		consentCommercialCommunications: profile.value.consentCommercialCommunications,
	} as Profile)

	onMounted(async () => {
		await getDestinations()
	})

	const disableContinueButton = computed(() => {
		if (!wizard.value) return true
		const currentStep = wizard.value.step + 1
		if (currentStep === 1 && !wishlistAdded.value.length) return true
		if (currentStep === 2 && !visitedAdded.value.length) return true
		return false
	})

	const sendData = async () => {
		await ProfileActions.updateProfile(form.value)
		await sendDestinations()
		emit('close')
	}

	const handleProfile = (profileForm: Profile): void => {
		cleanErrors(profileForm)
		form.value = { ...profileForm }
	}

	const submit = async () => {
		const errors = await ProfileActions.validateProfile(form.value)
		if (!errors) return sendData()
		formErrors.value = errors
	}

	const sendDestinations = async () => {
		const completeList = [...wishlistAdded.value, ...visitedAdded.value]

		if (completeList.length > 0) {
			await ProfileDestinationsActions.uploadDestination(profile.value!.id!, completeList)
		}
	}

	const cleanErrors = (profileForm: Profile) => {
		Object.keys(profileForm).forEach((key) => {
			const theKey = key as keyof Profile
			if (profileForm[theKey] !== form.value[theKey]) {
				formErrors.value[theKey] = ''
			}
		})
	}

	const getDestinations = async () => {
		const destinations = await ProfileDestinationsActions.getDestinations(profile.value!.id!)
		if (!destinations?.length) return
		wishList.value = destinations.filter((destination) => destination.type === 'wishlist')
		visitedList.value = destinations.filter((destination) => destination.type === 'visited')
	}

	const addWishlist = async (destination: ProfileDestination) => {
		wishList.value.push(destination)
		wishlistAdded.value.push(destination)
	}

	const addVisitedList = async (destination: ProfileDestination) => {
		visitedList.value.push(destination)
		visitedAdded.value.push(destination)
	}

	const deleteWishlistAdded = async (destination: ProfileDestination) => {
		wishList.value = wishList.value.filter(
			(item) => item.destinationId !== destination.destinationId
		)
		wishlistAdded.value = wishlistAdded.value.filter(
			(item) => item.destinationId !== destination.destinationId
		)
	}

	const deleteVisitedAdded = async (destination: ProfileDestination) => {
		visitedList.value = visitedList.value.filter(
			(item) => item.destinationId !== destination.destinationId
		)
		visitedAdded.value = visitedAdded.value.filter(
			(item) => item.destinationId !== destination.destinationId
		)
	}

	const skipStep = () => {
		wizard.value.goToNextStep()
	}
</script>
